//
// aircraft.ts — live aircraft
//
// Translation of `Networking/AircraftService.swift`, with the same coverage
// anchors and the same reasoning behind them.
//
// **On provider choice.** Dave named airplanes.live as primary and adsb.lol
// as standby. Probed 2026-08-21, that has to invert: airplanes.live now
// answers unauthenticated requests with "Please contact us at
// contact@airplanes.live", and its REST host answers "403: Check auth key."
// Their public API is key-gated. adsb.lol serves the same v2 JSON shape
// freely, so it carries the app today. On the web the switch would happen
// in nginx rather than here, because a browser cannot hold the key.
//
// **On anchor sampling.** ADS-B is a receiver network, not a satellite feed.
// There is no "every aircraft on Earth" endpoint, and a 250nm circle over
// the mid-Atlantic genuinely returns zero aircraft because nobody is
// listening there (verified). Global coverage means querying dense regions
// and merging — which is what `GLOBAL_ANCHORS` is.
//

import type { ContactKey, TrackedObject } from "../types";
import { ADSB_BASE, fetchJSON } from "./config";

interface RawAircraft {
  hex?: string;
  flight?: string;
  r?: string;
  t?: string;
  lat?: number;
  lon?: number;
  alt_baro?: number | string;
  alt_geom?: number;
  gs?: number;
  track?: number;
  baro_rate?: number;
  squawk?: string;
  category?: string;
  seen_pos?: number;
  dbFlags?: number;
}

interface RawResponse {
  ac?: RawAircraft[];
}

/**
 * Query centres for the global picture — one 250nm circle each (the
 * provider maximum), sited on dense receiver regions rather than spaced
 * evenly. Oceans are absent on purpose: no receivers, no returns.
 */
export const GLOBAL_ANCHORS: Array<{ name: string; lat: number; lon: number }> = [
  { name: "US Northeast", lat: 40.7, lon: -74.0 },
  { name: "US West", lat: 34.0, lon: -118.2 },
  { name: "US Central", lat: 32.9, lon: -97.0 },
  { name: "US Southeast", lat: 33.6, lon: -84.4 },
  { name: "Canada East", lat: 43.7, lon: -79.4 },
  { name: "Mexico / C. America", lat: 19.4, lon: -99.1 },
  { name: "Brazil", lat: -23.5, lon: -46.6 },
  { name: "UK / Ireland", lat: 51.5, lon: -0.5 },
  { name: "Central Europe", lat: 50.0, lon: 8.6 },
  { name: "Iberia / W. Med", lat: 40.4, lon: -3.7 },
  { name: "Nordics", lat: 59.6, lon: 17.9 },
  { name: "Turkey / E. Med", lat: 41.0, lon: 28.8 },
  { name: "Gulf", lat: 25.2, lon: 55.3 },
  { name: "India", lat: 28.6, lon: 77.1 },
  { name: "SE Asia", lat: 1.36, lon: 103.9 },
  { name: "Japan / Korea", lat: 35.6, lon: 139.8 },
  { name: "Australia East", lat: -33.9, lon: 151.2 },
  { name: "South Africa", lat: -26.1, lon: 28.2 },
];

/**
 * Render budget, not an API limit: the anchors can return several thousand
 * aircraft between them and the canvas redraws every frame. Highest-flying
 * win, because those are the tracks that read at global scale.
 */
export const MAX_CIVIL_AIRCRAFT = 420;

/**
 * Which of AIR's four second-tier buttons an aircraft belongs to. Build
 * 1.5, item 13.
 *
 * No new request and no new feed: the ADS-B emitter category is already in
 * every record we receive and was already being turned into text for the
 * info panel — it was simply never used to decide anything.
 *
 *   A3/A4/A5  large, high-vortex, heavy   -> commercial
 *   A1/A2     light, small                -> private / GA
 *   A7        rotorcraft                  -> rotorcraft
 *   dbFlags&1 or the /mil feed            -> military, which wins outright
 *
 * Aircraft that broadcast no category at all — a real and common gap —
 * fall to commercial if they carry an airline-shaped callsign and private
 * otherwise. The info panel keeps showing the raw class either way, so a
 * guess is never disguised as a reading.
 */
function layerFor(code: string | undefined, callsign: string, military: boolean): ContactKey {
  if (military) return "airMilitary";
  switch ((code ?? "").toUpperCase()) {
    case "A3":
    case "A4":
    case "A5":
      return "airCommercial";
    case "A1":
    case "A2":
    case "B1":
    case "B2":
    case "B4":
      return "airPrivate";
    case "A7":
      return "airRotorcraft";
    default:
      // An airline callsign is three letters then digits: "UAL271", "BAW15".
      return /^[A-Z]{3}\d/.test(callsign) ? "airCommercial" : "airPrivate";
  }
}

/** ADS-B emitter category codes, per DO-260B. */
function categoryLabel(code?: string): string {
  switch ((code ?? "").toUpperCase()) {
    case "A1": return "LIGHT AIRCRAFT (<15,500 LB)";
    case "A2": return "SMALL AIRCRAFT (15,500-75,000 LB)";
    case "A3": return "LARGE AIRCRAFT (75,000-300,000 LB)";
    case "A4": return "HIGH-VORTEX LARGE (B757)";
    case "A5": return "HEAVY AIRCRAFT (>300,000 LB)";
    case "A6": return "HIGH-PERFORMANCE AIRCRAFT";
    case "A7": return "ROTORCRAFT";
    case "B1": return "GLIDER / SAILPLANE";
    case "B2": return "LIGHTER-THAN-AIR";
    case "B4": return "ULTRALIGHT / PARAGLIDER";
    case "B6": return "UNMANNED AERIAL VEHICLE";
    case "B7": return "SPACE / TRANS-ATMOSPHERIC";
    case "C1":
    case "C2": return "SURFACE VEHICLE";
    default: return "COMMERCIAL AIR TRAFFIC";
  }
}

/**
 * Callsign prefix to airline name. ADS-B carries the ICAO callsign and no
 * operator name, so this is what turns "UAL271" into "United Airlines".
 * Deliberately short — the carriers likely to appear at global scale — and
 * returns undefined rather than guessing.
 */
export const AIRLINES_BY_ICAO: Record<string, string> = {
  AAL: "American Airlines", UAL: "United Airlines", DAL: "Delta Air Lines",
  SWA: "Southwest Airlines", JBU: "JetBlue Airways", ASA: "Alaska Airlines",
  FFT: "Frontier Airlines", NKS: "Spirit Airlines", SKW: "SkyWest Airlines",
  RPA: "Republic Airways", ENY: "Envoy Air", EDV: "Endeavor Air",
  ACA: "Air Canada", WJA: "WestJet", AMX: "Aeroméxico", VOI: "Volaris",
  BAW: "British Airways", VIR: "Virgin Atlantic", EZY: "easyJet",
  RYR: "Ryanair", DLH: "Lufthansa", AFR: "Air France", KLM: "KLM",
  IBE: "Iberia", VLG: "Vueling", SAS: "SAS", AZA: "ITA Airways",
  SWR: "SWISS", AUA: "Austrian Airlines", TAP: "TAP Air Portugal",
  THY: "Turkish Airlines", AFL: "Aeroflot", FIN: "Finnair",
  UAE: "Emirates", QTR: "Qatar Airways", ETD: "Etihad Airways",
  SVA: "Saudia", MSR: "EgyptAir", ELY: "El Al",
  SIA: "Singapore Airlines", CPA: "Cathay Pacific", JAL: "Japan Airlines",
  ANA: "All Nippon Airways", KAL: "Korean Air", AAR: "Asiana Airlines",
  CCA: "Air China", CES: "China Eastern", CSN: "China Southern",
  AIC: "Air India", IGO: "IndiGo", THA: "Thai Airways", MAS: "Malaysia Airlines",
  QFA: "Qantas", ANZ: "Air New Zealand", JST: "Jetstar",
  SAA: "South African Airways", ETH: "Ethiopian Airlines",
  TAM: "LATAM Brasil", GLO: "GOL", AZU: "Azul", ARG: "Aerolíneas Argentinas",
  FDX: "FedEx Express", UPS: "UPS Airlines", GTI: "Atlas Air",
  CLX: "Cargolux", CKS: "Kalitta Air", ABW: "AirBridgeCargo",
};

function airlineFor(callsign: string): string | undefined {
  const prefix = callsign.slice(0, 3).toUpperCase();
  if (prefix.length !== 3 || !/^[A-Z]{3}$/.test(prefix)) return undefined;
  return AIRLINES_BY_ICAO[prefix];
}

function freshness(seenPos?: number): string {
  if (seenPos === undefined) return "Live ADS-B";
  if (seenPos < 5) return "Live ADS-B";
  if (seenPos < 60) return `ADS-B ${seenPos.toFixed(0)}s ago`;
  return `ADS-B ${(seenPos / 60).toFixed(0)}m ago`;
}

function toTrackedObject(ac: RawAircraft, military: boolean): TrackedObject | null {
  if (ac.lat === undefined || ac.lon === undefined || !ac.hex) return null;

  // Callsigns arrive space-padded to eight characters ("UAL271  ").
  const callsign = (ac.flight ?? "").trim();
  const registration = (ac.r ?? "").trim() || undefined;
  const isMilitary = military || ((ac.dbFlags ?? 0) & 1) === 1;

  // "alt_baro" is a number in flight and the literal string "ground" on the
  // tarmac — the one genuinely awkward field in this format.
  const onGround = ac.alt_baro === "ground";
  const altFt = typeof ac.alt_baro === "number" ? ac.alt_baro : ac.alt_geom;

  const name = callsign && registration
    ? `${callsign} (${registration})`
    : callsign || registration || ac.hex.toUpperCase();

  const typeLabel = isMilitary
    ? ac.t ? `MILITARY / STATE AIRCRAFT — ${ac.t}` : "MILITARY / STATE AIRCRAFT"
    : ac.t ? `${ac.t} — ${categoryLabel(ac.category)}` : categoryLabel(ac.category);

  return {
    id: `ac-${ac.hex}`,
    icao24: ac.hex,
    name,
    category: isMilitary ? "military_aircraft" : "flight",
    layer: layerFor(ac.category, callsign, isMilitary),
    typeLabel,
    latitude: ac.lat,
    longitude: ac.lon,
    altitudeFt: altFt,
    speedKnots: ac.gs,
    headingDeg: ac.track,
    airline: airlineFor(callsign),
    flightNumber: callsign || undefined,
    dataSource: "adsb.lol (ADS-B)",
    lastUpdated: freshness(ac.seen_pos),
    registration,
    aircraftTypeCode: ac.t,
    squawk: ac.squawk,
    verticalRateFpm: ac.baro_rate,
    isOnGround: onGround,
    positionAgeSeconds: ac.seen_pos,
  };
}

/**
 * Is this record actually an aircraft in flight?
 *
 * An ADS-B feed carries more than aeroplanes. Ground vehicles, airport
 * infrastructure and fixed test transponders all broadcast on the same
 * frequency, and some of them report nonsense altitudes. Sorting by
 * altitude — which is how the render budget picks who makes the map — put
 * those straight at the top: the first check of the live site had "FFMUNT
 * (TWR)" as its headline contact, a tower transponder claiming 55,900 feet
 * while moving at 3 knots, squawking 7777.
 *
 * Two cheap tests remove essentially all of it:
 *
 *   - **It has to be moving.** Nothing is airborne at 40 knots. Ground
 *     vehicles and fixed installations fail this immediately, and no real
 *     aircraft in cruise is anywhere near it.
 *   - **Its altitude has to be possible.** 60,000 ft is above almost
 *     everything that flies; a record claiming more is reporting a fault,
 *     not a U-2.
 */
function isPlausibleAirborneAircraft(ac: RawAircraft): boolean {
  if (ac.lat === undefined || ac.lon === undefined) return false;
  if (ac.alt_baro === "ground") return false;

  const speed = ac.gs ?? 0;
  if (speed < 40) return false;

  const altitude = typeof ac.alt_baro === "number" ? ac.alt_baro : (ac.alt_geom ?? 0);
  if (altitude > 60000) return false;

  return true;
}

/**
 * How many anchor requests may be in flight at once.
 *
 * This used to be all eighteen at once, which is what rate-limited a
 * development machine on 2026-08-21. Even behind the caching proxy, a burst
 * of eighteen is a spike worth not creating — four at a time finishes the
 * sweep in well under a second and looks like a client rather than a
 * scraper.
 */
const MAX_CONCURRENT_ANCHOR_REQUESTS = 4;

/**
 * `Promise.all` with a ceiling on how many run at once. Results come back
 * in input order, so callers can still reason about which anchor produced
 * what.
 */
async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;

  async function worker(): Promise<void> {
    while (next < items.length) {
      const index = next++;
      results[index] = await fn(items[index]);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, () => worker()),
  );
  return results;
}

/**
 * Every civil aircraft visible across `GLOBAL_ANCHORS`, deduplicated by
 * ICAO hex. Failure is per-anchor: one that times out contributes nothing
 * and the rest still draw.
 */
export async function fetchFlights(): Promise<TrackedObject[]> {
  const batches = await mapWithConcurrency(
    GLOBAL_ANCHORS,
    MAX_CONCURRENT_ANCHOR_REQUESTS,
    (anchor) =>
      fetchJSON<RawResponse>(`${ADSB_BASE}/point/${anchor.lat}/${anchor.lon}/250`),
  );

  const seen = new Set<string>();
  const unique: RawAircraft[] = [];
  for (const batch of batches) {
    for (const ac of batch?.ac ?? []) {
      if (!ac.hex || seen.has(ac.hex)) continue;
      seen.add(ac.hex);
      unique.push(ac);
    }
  }

  return capByClass(unique.filter(isPlausibleAirborneAircraft))
    .map((ac) => toTrackedObject(ac, false))
    .filter((o): o is TrackedObject => o !== null);
}

/**
 * Trims the traffic picture to `MAX_CIVIL_AIRCRAFT`, **giving each of AIR's
 * civil buttons a share** rather than taking the highest-flying 420
 * outright.
 *
 * Build 1.5, found while checking item 13 on a live feed. The old cap was a
 * single altitude sort, on the sound reasoning that high tracks are the
 * ones that read at global scale. But helicopters fly at one to three
 * thousand feet and light aircraft not much higher, so an altitude sort cut
 * *every* rotorcraft and all but fifteen of the private traffic: the
 * Rotorcraft button was empty every time it was pressed, and Private / GA
 * was nearly so. A button that exists and can never show anything is worse
 * than no button.
 *
 * Altitude still decides **within** each class, so the reasoning survives
 * where it applies. Unused reservations go back to the pool, so on a quiet
 * night for helicopters the airliners still fill the map.
 */
function capByClass(list: RawAircraft[]): RawAircraft[] {
  const RESERVED: Partial<Record<ContactKey, number>> = {
    airPrivate: 70,
    airRotorcraft: 30,
  };

  const byAltitude = (a: RawAircraft, b: RawAircraft) => {
    const aAlt = typeof a.alt_baro === "number" ? a.alt_baro : 0;
    const bAlt = typeof b.alt_baro === "number" ? b.alt_baro : 0;
    return bAlt - aAlt;
  };

  const buckets = new Map<ContactKey, RawAircraft[]>();
  for (const ac of list) {
    const key = layerFor(ac.category, (ac.flight ?? "").trim(), false);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(ac);
    else buckets.set(key, [ac]);
  }
  for (const bucket of buckets.values()) bucket.sort(byAltitude);

  const picked: RawAircraft[] = [];
  const leftover: RawAircraft[] = [];
  for (const [key, bucket] of buckets) {
    const share = RESERVED[key];
    if (share === undefined) {
      leftover.push(...bucket);
    } else {
      picked.push(...bucket.slice(0, share));
      leftover.push(...bucket.slice(share));
    }
  }

  leftover.sort(byAltitude);
  picked.push(...leftover.slice(0, Math.max(0, MAX_CIVIL_AIRCRAFT - picked.length)));
  return picked;
}

/** Military and state aircraft — one global endpoint, no anchor sampling. */
export async function fetchMilitary(): Promise<TrackedObject[]> {
  const response = await fetchJSON<RawResponse>(`${ADSB_BASE}/mil`);
  return (response?.ac ?? [])
    // Same plausibility filter as civil traffic. The military feed is
    // especially full of parked airframes broadcasting from a ramp.
    .filter(isPlausibleAirborneAircraft)
    .map((ac) => toTrackedObject(ac, true))
    .filter((o): o is TrackedObject => o !== null);
}
